import { useState, useEffect } from 'react';

/* ─── 共用：迴圈 phase hook ─── */
function useCycle(delays: number[]) {
  const [phase, setPhase] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const ts: ReturnType<typeof setTimeout>[] = [];
    let t = 0;
    delays.forEach((d, i) => {
      t += d;
      ts.push(setTimeout(() => setPhase(i + 1), t));
    });
    ts.push(setTimeout(() => { setPhase(0); setTick(c => c + 1); }, t + 2200));
    return () => ts.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);
  return phase;
}

/* ─── 共用：Demo 視窗外框 ─── */
function DemoWin({ accent, label, children, fixedHeight }: { accent: string; label: string; children: React.ReactNode; fixedHeight?: string }) {
  return (
    <div className="rounded-2xl overflow-hidden border bg-gray-900" style={{ borderColor: accent + '40' }}>
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b" style={{ background: accent + '12', borderColor: accent + '25' }}>
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-green-400/60"></span>
        <span className="ml-2 text-[10px] font-bold" style={{ color: accent }}>{label}</span>
        <span className="ml-auto flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }}></span>
          <span className="text-[9px] text-white/30">LIVE</span>
        </span>
      </div>
      {/* 固定高度容器，overflow-hidden 確保動畫重置時不影響外部佈局 */}
      <div className={`p-4 overflow-hidden ${fixedHeight ?? 'min-h-[220px]'}`}>{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════
   1. AI 智能配對
══════════════════════════════════════════ */
const MATCH_CARDS = [
  { title: '大安區精裝兩房', loc: '台北市大安區', price: '24,500', pct: 96 },
  { title: '信義區捷運套房', loc: '台北市信義區', price: '21,800', pct: 88 },
  { title: '中山區明亮三房', loc: '台北市中山區', price: '26,800', pct: 79 },
];

export function MatchDemo() {
  const phase = useCycle([700, 800, 650, 650]);
  return (
    <DemoWin accent="#2dd4bf" label="AI 智能配對">
      <div className="space-y-2.5">
        <div className="flex flex-wrap gap-1.5 mb-1">
          {['台北市', '2 房', '≤25K/月', '近捷運'].map(tag => (
            <span key={tag} className={`px-2 py-0.5 rounded-full text-[10px] border transition-all duration-300 ${phase >= 1 ? 'bg-teal-500/20 border-teal-500/30 text-teal-300' : 'bg-white/5 border-white/10 text-white/25'}`}>{tag}</span>
          ))}
          {/* 保留空間：用 invisible 而非條件渲染，避免 reflow */}
          <span className={`flex items-center gap-1 text-[10px] text-white/40 transition-all duration-200 ${phase === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse inline-block"></span>AI 分析中...
          </span>
        </div>
        {MATCH_CARDS.map((c, i) => (
          <div key={i} className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 border transition-all duration-500 ${phase >= i + 2 ? 'opacity-100 translate-y-0 bg-white/5 border-teal-500/15' : 'opacity-0 translate-y-3 bg-transparent border-transparent pointer-events-none'}`}>
            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-teal-500/20 flex items-center justify-center">
              <i className="ri-home-4-line text-teal-400 text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{c.title}</p>
              <p className="text-[10px] text-white/40">{c.loc} · NT${c.price}/月</p>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-sm font-black text-teal-400">{c.pct}<span className="text-[9px]">%</span></p>
              <p className="text-[9px] text-white/30">配對度</p>
            </div>
          </div>
        ))}
        <p className={`text-center text-[10px] text-teal-400/70 pt-1 transition-all duration-300 ${phase >= 5 ? 'opacity-100' : 'opacity-0'}`}>
          ✓ 共找到 3 個高匹配房源
        </p>
      </div>
    </DemoWin>
  );
}

/* ══════════════════════════════════════════
   2. AI 租客審核
══════════════════════════════════════════ */
const CRITERIA = [
  { label: '職業穩定性', score: 87, color: '#4ade80' },
  { label: '收入水準',   score: 82, color: '#4ade80' },
  { label: '信用紀錄',   score: 91, color: '#2dd4bf' },
  { label: '租屋歷史',   score: 78, color: '#fbbf24' },
];

export function ScreeningDemo() {
  const phase = useCycle([500, 700, 700, 700, 700, 900]);
  return (
    <DemoWin accent="#4ade80" label="AI 租客審核">
      <div className="space-y-2.5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-white/8">
          <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-line text-green-400 text-sm"></i>
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-white">張○明 · 申請看房</p>
            <p className="text-[10px] text-white/40">已提交 AI 審核請求</p>
          </div>
          <span className={`text-[9px] text-green-400 flex items-center gap-1 flex-shrink-0 transition-all duration-200 ${phase >= 1 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>分析中
          </span>
        </div>
        {CRITERIA.map((c, i) => (
          <div key={i} className={`transition-all duration-400 ${phase >= i + 2 ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/55">{c.label}</span>
              <span className="text-[10px] font-bold" style={{ color: c.color }}>{c.score}%</span>
            </div>
            <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700 delay-200"
                style={{ width: phase >= i + 2 ? `${c.score}%` : '0%', background: c.color }}></div>
            </div>
          </div>
        ))}
        <div className={`flex items-center gap-2.5 bg-green-500/10 border border-green-500/25 rounded-xl px-3 py-2.5 transition-all duration-500 ${phase >= 6 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <i className="ri-shield-check-fill text-green-400 text-lg"></i>
          <div className="flex-1">
            <p className="text-xs font-bold text-green-300">低風險租客</p>
            <p className="text-[10px] text-white/40">AI 建議：可進行看房安排</p>
          </div>
          <span className="text-xl font-black text-green-400">85</span>
        </div>
      </div>
    </DemoWin>
  );
}

/* ══════════════════════════════════════════
   3. AI 標題生成
══════════════════════════════════════════ */
const AI_TITLES = [
  '大安區精裝兩房，近捷運，採光極佳 ✨',
  '【大安】屋主直租・零仲介，全新裝潢明亮兩房',
  '台北大安 精品兩房出租，生活機能超強 🏠',
];

export function TitleDemo() {
  const phase = useCycle([800, 700, 1000, 1000, 1000]);
  const [typed, setTyped] = useState(['', '', '']);

  useEffect(() => {
    if (phase < 3) { setTyped(['', '', '']); return; }
    const idx = phase - 3;
    const target = AI_TITLES[idx];
    if (!target) return;
    let i = 0;
    const iv = setInterval(() => {
      setTyped(prev => { const n = [...prev]; n[idx] = target.slice(0, i + 1); return n; });
      i++;
      if (i >= target.length) clearInterval(iv);
    }, 28);
    return () => clearInterval(iv);
  }, [phase]);

  return (
    <DemoWin accent="#fbbf24" label="AI 標題生成">
      <div className="space-y-2.5">
        <div className="bg-white/5 border border-white/8 rounded-xl px-3 py-2">
          <p className="text-[9px] text-amber-400/70 mb-0.5">房源資訊輸入</p>
          <p className="text-[10px] text-white/55">台北大安 · 2房20坪 · NT$25,000/月 · 近捷運</p>
        </div>
        {/* 等待提示：用 opacity 保留空間 */}
        <p className={`text-[10px] text-white/25 text-center py-1 transition-all duration-200 ${phase === 0 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden py-0'}`}>等待分析房源...</p>
        <div className={`flex items-center justify-center gap-1.5 py-1 transition-all duration-200 ${phase === 1 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden py-0'}`}>
          {[0, 1, 2].map(i => <span key={i} className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: `${i * 150}ms` }}></span>)}
          <span className="text-[10px] text-white/40 ml-1">AI 生成標題中...</span>
        </div>
        {AI_TITLES.map((title, i) => (
          /* 始終保留 DOM，用 opacity + max-height 控制顯示 */
          <div key={i}
            className={`flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5 transition-all duration-300 ${phase >= i + 2 ? 'opacity-100 max-h-24' : 'opacity-0 max-h-0 overflow-hidden py-0 border-transparent'}`}
          >
            <span className="text-[10px] font-black text-amber-500/50 flex-shrink-0 mt-0.5">#{i + 1}</span>
            <div className="flex-1 min-w-0">
              {phase >= i + 3 ? (
                <p className="text-[11px] text-white leading-snug">
                  {typed[i]}
                  {typed[i].length < title.length && (
                    <span className="inline-block w-0.5 h-3 bg-amber-400 animate-pulse ml-0.5 align-middle"></span>
                  )}
                </p>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block"></span>
                  <span className="text-[10px] text-white/30">生成中...</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </DemoWin>
  );
}

/* ══════════════════════════════════════════
   4. AI 推廣文案
══════════════════════════════════════════ */
const PROMO_LINES = [
  '🏠 台北大安 精裝兩房出租',
  'NT$25,000/月 | 20坪 | 2房1廳1衛',
  '',
  '✨ 屋主直租・零仲介費',
  '🚇 近捷運 | 🏪 樓下全聯 | 🌿 採光絕佳',
  '🛋️ 全新裝潢・家電齊全',
  '',
  '有意洽詢歡迎私訊 👋',
];

export function PromoDemo() {
  const phase = useCycle([600, 800, 300, 300, 300, 300, 300, 300, 300, 1200]);
  return (
    <DemoWin accent="#fb923c" label="AI 推廣文案">
      <div className="space-y-2.5">
        <div className="flex gap-2">
          {[{ icon: 'ri-threads-line', name: 'Threads' }, { icon: 'ri-facebook-circle-line', name: 'Facebook' }].map(p => (
            <div key={p.name} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] transition-all duration-400 ${phase >= 1 ? 'bg-orange-500/15 border-orange-500/25 text-orange-300' : 'bg-white/5 border-white/10 text-white/25'}`}>
              <i className={`${p.icon} text-sm`}></i>{p.name}
            </div>
          ))}
        </div>
        {/* 生成中提示：保留空間 */}
        <div className={`flex items-center gap-1.5 transition-all duration-200 ${phase === 1 ? 'opacity-100 py-2' : 'opacity-0 h-0 overflow-hidden py-0'}`}>
          {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: `${i * 150}ms` }}></span>)}
          <span className="text-[10px] text-white/40">AI 撰寫文案中...</span>
        </div>
        {/* 文案區塊：始終保留，用 opacity 控制 */}
        <div className={`bg-white/5 border border-white/8 rounded-xl p-3 space-y-0.5 min-h-[110px] transition-all duration-300 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          {PROMO_LINES.map((line, i) => (
            <p key={i} className={`text-[11px] leading-relaxed transition-all duration-300 ${phase >= i + 2 ? 'opacity-100' : 'opacity-0'} ${line === '' ? 'h-1.5' : 'text-white/80'}`}>{line}</p>
          ))}
        </div>
        <div className={`flex items-center gap-1.5 text-orange-300 transition-all duration-300 ${phase >= 10 ? 'opacity-100' : 'opacity-0'}`}>
          <i className="ri-checkbox-circle-fill text-sm"></i>
          <span className="text-[10px] font-bold">已排程發布至 Threads 及 Facebook</span>
        </div>
      </div>
    </DemoWin>
  );
}

/* ══════════════════════════════════════════
   5. AI 租屋客服問答
══════════════════════════════════════════ */
const CHAT_QA = [
  { q: '這間在幾樓？', a: '位於3樓，共12層，有電梯 🏢' },
  { q: '附近有捷運嗎？', a: '步行約5分鐘到信義安和站（紅線）🚇' },
  { q: '可以養貓嗎？', a: '開放小型寵物，需簽寵物附約 🐱' },
];

export function ChatDemo() {
  const phase = useCycle([500, 800, 800, 800, 800, 800, 800]);
  return (
    <DemoWin accent="#34d399" label="AI 租屋客服" fixedHeight="h-[290px]">
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-2.5 border-b border-white/8 mb-1">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-white text-xs"></i>
          </div>
          <span className="text-[11px] font-bold text-white">藍舍 AI 客服</span>
          <span className="ml-auto flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-[9px] text-green-400">24/7 在線</span>
          </span>
        </div>
        {CHAT_QA.map((qa, i) => {
          const showQ = phase >= 2 * i + 1;
          const showTyping = phase === 2 * i + 1;
          const showA = phase >= 2 * i + 2;
          return (
            <div key={i} className={`space-y-1.5 transition-all duration-300 ${showQ ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-end">
                <div className="bg-emerald-600/25 border border-emerald-500/20 rounded-2xl rounded-tr-sm px-3 py-1.5 max-w-[78%]">
                  <p className="text-[11px] text-white">{qa.q}</p>
                </div>
              </div>
              {/* 打字指示器：固定高度 h-5，用 opacity 控制，不縮放 */}
              <div className={`flex items-center gap-1 ml-7 h-5 transition-opacity duration-200 ${showTyping ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                {[0, 1, 2].map(j => <span key={j} className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: `${j * 150}ms` }}></span>)}
              </div>
              {/* AI 回覆：固定高度，opacity 控制 */}
              <div className={`flex items-end gap-1.5 transition-opacity duration-300 ${showA ? 'opacity-100' : 'opacity-0'}`}>
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-2-line text-white text-[9px]"></i>
                </div>
                <div className="bg-white/8 border border-white/10 rounded-2xl rounded-bl-sm px-3 py-1.5 max-w-[78%]">
                  <p className="text-[11px] text-white/90">{qa.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DemoWin>
  );
}

/* ══════════════════════════════════════════
   6. AI 實價查詢分析
══════════════════════════════════════════ */
const PRICE_BARS = [
  { label: '同路段均價', min: 22000, max: 26500, color: '#f87171' },
  { label: '同格局均價', min: 23000, max: 27000, color: '#fb923c' },
  { label: '同坪數均價', min: 23500, max: 27500, color: '#fbbf24' },
];

export function PriceDemo() {
  const phase = useCycle([700, 900, 600, 600, 600, 1100]);
  return (
    <DemoWin accent="#f87171" label="AI 實價分析" fixedHeight="h-[280px]">
      <div className="space-y-2.5">
        <div className={`flex items-center gap-2 rounded-xl px-3 py-2 border transition-all duration-300 ${phase >= 1 ? 'bg-rose-500/10 border-rose-500/20' : 'bg-white/5 border-white/8'}`}>
          <i className={`ri-map-pin-2-line text-xs ${phase >= 1 ? 'text-rose-400' : 'text-white/30'}`}></i>
          <p className="text-[11px] text-white/55">台北市大安區仁愛路四段...</p>
          <span className={`ml-auto w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse transition-opacity duration-200 ${phase === 1 ? 'opacity-100' : 'opacity-0'}`}></span>
        </div>
        {/* 查詢中：固定高度 h-8，opacity 控制，不縮放，避免 reflow */}
        <div className={`flex items-center justify-center gap-1.5 h-8 transition-opacity duration-200 ${phase === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: `${i * 150}ms` }}></span>)}
          <span className="text-[10px] text-white/40">查詢實價登錄資料...</span>
        </div>
        {PRICE_BARS.map((b, i) => (
          <div key={i} className={`transition-opacity duration-500 ${phase >= i + 2 ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/50">{b.label}</span>
              <span className="text-[10px] font-bold text-white/65">{(b.min / 1000).toFixed(1)}k～{(b.max / 1000).toFixed(1)}k</span>
            </div>
            <div className="h-2 bg-white/8 rounded-full overflow-hidden relative">
              <div className="absolute top-0 h-full rounded-full transition-all duration-800 delay-100"
                style={{ left: `${((b.min - 20000) / 10000) * 100}%`, width: phase >= i + 2 ? `${((b.max - b.min) / 10000) * 100}%` : '0%', background: b.color }}></div>
            </div>
          </div>
        ))}
        <div className={`flex items-center gap-2.5 bg-rose-500/10 border border-rose-500/25 rounded-xl px-3 py-2.5 transition-all duration-500 ${phase >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <i className="ri-sparkling-2-fill text-rose-400 text-base flex-shrink-0"></i>
          <div>
            <p className="text-[10px] text-white/45">AI 建議定價區間</p>
            <p className="text-sm font-black text-rose-300">NT$23,500 ～ 25,500 <span className="text-[10px] font-normal text-white/40">/月</span></p>
          </div>
        </div>
      </div>
    </DemoWin>
  );
}